import React, { useEffect, useState } from 'react';
import { Download, Check, Smartphone, Monitor, Tablet, AlertCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Alert, AlertDescription } from '../components/ui/alert';
import { faqData, troubleshootingData, updatesData } from '../data/mock';
import AdSense from '../components/AdSense';

const DOWNLOAD_HISTORY_KEY = 'videovault_download_history';
const DOWNLOAD_HISTORY_LIMIT = 10;

const Home = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [videoInfo, setVideoInfo] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState([]);

  useEffect(() => {
    try {
      const savedHistory = window.localStorage.getItem(DOWNLOAD_HISTORY_KEY);
      if (!savedHistory) return;

      const parsedHistory = JSON.parse(savedHistory);
      if (Array.isArray(parsedHistory)) {
        setDownloadHistory(parsedHistory.slice(0, DOWNLOAD_HISTORY_LIMIT));
      }
    } catch (storageError) {
      console.error('No se pudo leer el historial de descargas:', storageError);
    }
  }, []);

  const saveDownloadToHistory = ({ title, url, thumbnail, format, quality }) => {
    const historyItem = {
      id: `${Date.now()}-${url}`,
      title: title || 'Video sin titulo',
      url,
      format: format || 'PENDIENTE',
      quality: quality || 'Pendiente de elegir',
      thumbnail: thumbnail || '',
      downloadedAt: new Date().toISOString()
    };

    try {
      setDownloadHistory((currentHistory) => {
        const updatedHistory = [historyItem, ...currentHistory].slice(0, DOWNLOAD_HISTORY_LIMIT);
        window.localStorage.setItem(DOWNLOAD_HISTORY_KEY, JSON.stringify(updatedHistory));
        return updatedHistory;
      });
    } catch (storageError) {
      console.error('No se pudo guardar el historial de descargas:', storageError);
    }
  };

  const clearDownloadHistory = () => {
    setDownloadHistory([]);
    window.localStorage.removeItem(DOWNLOAD_HISTORY_KEY);
  };

  const handleDownload = async () => {
    if (!videoUrl.trim()) {
      setError('Por favor, pegá un enlace válido');
      return;
    }

    setIsProcessing(true);
    setError('');
    setVideoInfo(null);

    try {
      const response = await axios.post(`/api/video/info`, {
        url: videoUrl
      });

      if (response.data.success) {
        setVideoInfo(response.data);
        saveDownloadToHistory({
          title: response.data.video_title,
          url: videoUrl,
          thumbnail: response.data.thumbnail
        });
      } else {
        setError(response.data.error || 'Error obteniendo información del video');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Error al procesar el video. Verificá que el enlace sea válido y el video sea público.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFormatDownload = async (selectedFormat) => {
    if (!selectedFormat) return;

    setDownloading(true);
    setError('');
    try {
      const response = await fetch(`/api/video/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: videoUrl,
          format_id: selectedFormat.format_id || 'best'
        })
      });

      if (!response.ok) {
        // Intentar leer el error como JSON, si no es posible usar mensaje genérico
        let errorMessage = 'Error al descargar el video';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Obtener el blob
      const blob = await response.blob();

      // Crear un link de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Sanitizar el nombre del archivo y agregar prefijo videovault
      const safeTitle = (videoInfo.video_title || 'video').replace(/[^a-z0-9]/gi, '_').substring(0, 80);
      const filename = `videovault_${safeTitle}.${selectedFormat.format}`;
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error descargando:', err);
      setError(err.message || 'Error al descargar el video. Por favor intentá de nuevo.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Banner superior después del header */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <AdSense
            slot={process.env.REACT_APP_ADSENSE_SLOT_TOP}
            format="horizontal"
            style={{ minHeight: '90px' }}
            className="max-w-6xl mx-auto"
          />
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">
              Descargá videos públicos de Instagram y Facebook en línea
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Rápido, simple y sin instalar nada
            </p>
          </div>

          <div className="max-w-2xl mx-auto mb-8">
            <p className="text-base text-gray-700 mb-8 leading-relaxed">
              En Video Vault podés guardar videos públicos de Instagram y Facebook pegando el enlace y
              eligiendo la calidad disponible. Incluimos guías paso a paso, respuestas a problemas comunes
              y un enfoque claro en el uso responsable y la privacidad.
            </p>

            {/* Download Tool */}
            <Card className="shadow-lg border-2">
              <CardHeader>
                <CardTitle>Pegá el enlace del video público aquí</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="https://instagram.com/reel/... o pegá un enlace de Facebook"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      className="flex-1"
                      disabled={isProcessing}
                    />
                    <Button
                      onClick={handleDownload}
                      disabled={isProcessing}
                      className="bg-black hover:bg-gray-800 transition-colors"
                    >
                      {isProcessing ? (
                        'Procesando...'
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Descargar
                        </>
                      )}
                    </Button>
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {videoInfo && (
                    <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                      <div className="flex items-start gap-4 mb-4">
                        {videoInfo.thumbnail && (
                          <img
                            src={videoInfo.thumbnail}
                            alt="Video thumbnail"
                            className="w-24 h-24 object-cover rounded"
                          />
                        )}
                        <div>
                          <h3 className="font-semibold text-black">{videoInfo.video_title}</h3>
                          {videoInfo.duration && (
                            <p className="text-sm text-gray-600 mt-1">Duración: {videoInfo.duration}</p>
                          )}
                          {videoInfo.video_count > 1 && (
                            <p className="text-sm font-medium text-black mt-1">
                              Encontramos {videoInfo.video_count} videos en este carrusel.
                            </p>
                          )}
                          <p className="text-sm text-gray-600 mt-1">Elegí el video y la calidad para descargar:</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {videoInfo.formats.map((format, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            className="w-full justify-between hover:bg-gray-100 transition-colors"
                            onClick={() => handleFormatDownload(format)}
                            disabled={downloading}
                          >
                            <span>{format.quality} ({format.format.toUpperCase()})</span>
                            <span className="text-gray-600">{format.size}</span>
                          </Button>
                        ))}
                      </div>
                      {downloading && (
                        <p className="text-sm text-gray-600 mt-4 text-center">
                          Descargando video... Por favor esperá.
                        </p>
                      )}
                    </div>
                  )}

                  {downloadHistory.length > 0 && (
                    <div className="mt-6 rounded-lg border bg-white p-4">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-black">Historial reciente</h3>
                          <p className="text-sm text-gray-600">
                            Tus ultimos 10 videos consultados se guardan solo en este navegador y no son accesibles para otras personas desde nuestro sitio.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={clearDownloadHistory}
                          className="shrink-0"
                        >
                          Limpiar historial
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {downloadHistory.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-start gap-3 rounded-lg border border-gray-200 p-3"
                          >
                            {item.thumbnail ? (
                              <img
                                src={item.thumbnail}
                                alt={item.title}
                                className="h-16 w-16 rounded object-cover"
                              />
                            ) : (
                              <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-100">
                                <Download className="h-5 w-5 text-gray-500" />
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium text-black">{item.title}</p>
                              <p className="text-sm text-gray-600">
                                {item.quality} ({item.format})
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                {new Date(item.downloadedAt).toLocaleString()}
                              </p>
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 inline-block text-sm text-black underline"
                              >
                                Ver enlace original
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Anuncio entre formulario y "Cómo usarlo" */}
      <div className="py-8 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <AdSense
            slot={process.env.REACT_APP_ADSENSE_SLOT_CONTENT_1}
            format="rectangle"
            style={{ minHeight: '250px' }}
          />
        </div>
      </div>

      {/* How to Use */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-12">
            ¿Cómo usarlo?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Copiá el enlace',
                description: 'Copiá el enlace desde Instagram o Facebook (publicación o Reel público).'
              },
              {
                step: '2',
                title: 'Pegalo aquí',
                description: 'Pegalo en el campo de arriba y presioná Descargar.'
              },
              {
                step: '3',
                title: 'Descargá',
                description: 'Elegí la opción disponible y guardá el archivo en tu dispositivo.'
              }
            ].map((item) => (
              <Card key={item.step} className="text-center border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-black text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <CardTitle>{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-12">
            Beneficios
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Funciona desde PC y celular (sin instalar apps)',
              'Instrucciones claras y solución de errores comunes',
              'No alojamos videos ni guardamos tu historial en nuestros servidores',
              'Uso responsable: recomendado para contenido propio o con permiso'
            ].map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check className="h-6 w-6 text-black flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guides */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-12">
            Guías rápidas
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2">
              <CardHeader>
                <Monitor className="h-10 w-10 mb-4 text-black" />
                <CardTitle>PC / Mac</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Copiá el enlace del video público → pegalo aquí → elegí calidad →
                  guardá el archivo en tu carpeta de descargas.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <Smartphone className="h-10 w-10 mb-4 text-black" />
                <CardTitle>Android</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Pegá el enlace → seleccioná la opción disponible → si el navegador abre el video
                  en lugar de descargarlo, probá "Mantener presionado → Descargar" (según navegador).
                </p>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <Tablet className="h-10 w-10 mb-4 text-black" />
                <CardTitle>iPhone / iPad</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  iOS puede manejar descargas diferente según el navegador. Recomendamos Safari y
                  guardar el archivo en "Archivos". Si se abre como reproducción, revisá las opciones
                  del reproductor o intentá de nuevo con otra calidad disponible.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Responsible Use */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Card className="border-2 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">Uso responsable</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Este servicio está pensado para videos públicos y para usos legítimos (por ejemplo,
                contenido propio o con autorización del autor). El usuario es responsable de cumplir
                los derechos de autor y términos de la plataforma.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Anuncio antes del FAQ */}
      <div className="py-8 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <AdSense
            slot={process.env.REACT_APP_ADSENSE_SLOT_CONTENT_2}
            format="rectangle"
            style={{ minHeight: '250px' }}
          />
        </div>
      </div>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-12">
            Preguntas Frecuentes
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqData.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-12">
            Solución de problemas
          </h2>
          <div className="space-y-3">
            {troubleshootingData.map((tip, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg border">
                <AlertCircle className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Updates */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-12">
            Actualizaciones recientes
          </h2>
          <div className="space-y-4">
            {updatesData.map((update, index) => (
              <div key={index} className="flex items-start gap-4 bg-gray-50 p-4 rounded-lg border">
                <TrendingUp className="h-5 w-5 text-black flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-black">{update.date}</p>
                  <p className="text-gray-700 mt-1">{update.update}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Anuncio antes del footer */}
      <div className="py-8 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <AdSense
            slot={process.env.REACT_APP_ADSENSE_SLOT_BOTTOM}
            format="horizontal"
            style={{ minHeight: '90px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;
